'use client';

import React, { useState, useEffect } from 'react';
import BlogCard, { type BlogPost } from './BlogCard';
import BlogFilters from './BlogFilters';
import BlogSidebar from './BlogSidebar';

interface BlogInteractiveProps {
  initialPosts: BlogPost[];
}

const BlogInteractive = ({ initialPosts }: BlogInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPosts, setFilteredPosts] = useState(initialPosts);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    let filtered = initialPosts;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(
        (post) => post.category.toLowerCase().replace(/\s+/g, '-') === selectedCategory
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (post) =>
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.category.toLowerCase().includes(query)
      );
    }

    setFilteredPosts(filtered);
  }, [selectedCategory, searchQuery, initialPosts, isHydrated]);

  const popularPosts = [
  {
    id: '1',
    title: 'Essential Puppy Training Tips for New Labrador Owners',
    image: "https://images.unsplash.com/photo-1677235329691-5d31bc033e11",
    imageAlt: 'Yellow Labrador puppy sitting attentively during training session outdoors',
    slug: 'essential-puppy-training-tips',
    publishDate: '2026-01-15'
  },
  {
    id: '2',
    title: 'Understanding Labrador Health: Common Issues and Prevention',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14bded7e0-1768592579190.png",
    imageAlt: 'Healthy black Labrador being examined by veterinarian in clinic',
    slug: 'labrador-health-common-issues',
    publishDate: '2026-01-10'
  },
  {
    id: '3',
    title: 'The Complete Guide to Labrador Nutrition',
    image: "/assets/images/Labrador_retriever_7_-1769800700557.jpg",
    imageAlt: 'White Labrador retriever puppy with tennis ball outdoors',
    slug: 'complete-guide-labrador-nutrition',
    publishDate: '2026-01-05'
  }];


  const categories = [
  { name: 'Puppy Care', count: 12, slug: 'puppy-care' },
  { name: 'Training', count: 8, slug: 'training' },
  { name: 'Health', count: 10, slug: 'health' },
  { name: 'Breed Information', count: 6, slug: 'breed-info' },
  { name: 'Nutrition', count: 7, slug: 'nutrition' }];


  const featuredPost = filteredPosts[0];
  const regularPosts = filteredPosts.slice(1);

  if (!isHydrated) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8 h-64 animate-pulse rounded-xl bg-muted"></div>
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="grid gap-6 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) =>
              <div key={i} className="h-96 animate-pulse rounded-xl bg-muted"></div>
              )}
            </div>
          </div>
          <div className="space-y-6">
            <div className="h-64 animate-pulse rounded-xl bg-muted"></div>
            <div className="h-96 animate-pulse rounded-xl bg-muted"></div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="container mx-auto px-4 py-12">
      <BlogFilters
        selectedCategory={selectedCategory}
        searchQuery={searchQuery}
        onCategoryChange={setSelectedCategory}
        onSearchChange={setSearchQuery} />


      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {featuredPost &&
          <div className="mb-8">
              <BlogCard post={featuredPost} featured />
            </div>
          }

          {regularPosts.length > 0 ?
          <div className="grid gap-6 md:grid-cols-2">
              {regularPosts.map((post) =>
            <BlogCard key={post.id} post={post} />
            )}
            </div> :

          <div className="rounded-xl bg-card p-12 text-center shadow-warm">
              <p className="text-muted-foreground">
                No articles match your search. Try different filters or keywords.
              </p>
            </div>
          }
        </div>

        <BlogSidebar popularPosts={popularPosts} categories={categories} />
      </div>
    </div>);

};

export default BlogInteractive;